import React, { useEffect } from "react";
import { ContractingSubFormBaseProps } from "features/contracting/models/ContractingSubFormBaseProps";
import PageCard from "features/shared/components/card/PageCard";
import FormGroup from "features/shared/forms/components/FormGroup";
import { useTranslation } from "react-i18next";
import DistributionRateTypeEnumDTO from "features/shared/enums/dynamic/DistributionRateTypeEnumDTO";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import Select from "react-select";
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import { setYearlyConsumptionNTValidation } from "features/contracting/sections/contractingForms/placeOfDeliveryForm/placeOfDeliveryFormValidation";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import { Commodity } from "features/shared/enums/commodity";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import {
  getPlaceOfDeliveryTrans,
  getYearlyConsumptionTrans,
  isMultipleDistributionRate,
} from "features/contracting/sections/contractingForms/placeOfDeliveryForm/placeOfDeliveryFormHelper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

interface Props extends ContractingSubFormBaseProps {
  commodity: Commodity;
  distributionRates: DistributionRateTypeEnumDTO[];
  consumptionCharacteristics: UserEnumDTO[];
}

const PlaceOfDeliveryDataSubForm = ({
  formHandler,
  commodity,
  distributionRates,
  consumptionCharacteristics,
}: Props) => {
  // translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "placeOfDeliveryDataSubForm");
  const tPhases = useTranslation("placeOfDelivery\\enums\\phases").t;

  // form hook props
  const {
    bindFieldNew,
    bindReactSelectField,
    bindCheckField,
    errors,
    getFormValue,
    setValidationRule,
    clearFormValues,
  } = { ...formHandler };

  // side-effects
  // handling validation/clearing of yearlyConsumptionNT
  useEffect(() => {
    if (isMultipleRate()) {
      setYearlyConsumptionNTValidation(setValidationRule);
    } else {
      setYearlyConsumptionNTValidation(setValidationRule, "remove");
      clearFormValues(["yearlyConsumptionNT"]);
    }
  }, [getFormValue("distributionRate")]);

  // helpers
  const isElectricity = () => {
    return commodity === "Electricity";
  };

  const isGas = () => {
    return commodity === "Gas";
  };

  const isMultipleRate = () => {
    const rateCode = getFormValue<string>("distributionRate");
    return isMultipleDistributionRate(commodity, rateCode, distributionRates);
  };

  // render
  return (
    <PageCard
      title={tLabels("cardTitle")}
      bodyClassName="pt-4 px-4"
      cardClassName="mb-4"
    >
      <div className="row no-gutters mx-n2">
        <div className="col-md-6 px-2">
          <FormGroup
            labelText={getPlaceOfDeliveryTrans(commodity, tLabels, "label")}
            inputId="placeOfDeliveryIdentification"
            validationError={errors["placeOfDeliveryIdentification"]}
          >
            <input
              id="placeOfDeliveryIdentification"
              className="form-control"
              placeholder={getPlaceOfDeliveryTrans(
                commodity,
                tLabels,
                "placeholder"
              )}
              {...bindFieldNew("placeOfDeliveryIdentification")}
            />
          </FormGroup>
        </div>
        <div className="col-md-6 px-2">
          {/* Electricity */}
          <FormGroup
            labelText={tLabels("distributionRate.label")}
            inputId="distributionRate"
            validationError={errors["distributionRate"]}
            visible={isElectricity()}
          >
            <Select
              {...bindReactSelectField<DistributionRateTypeEnumDTO>(
                "distributionRate",
                "code",
                distributionRates
              )}
              getOptionLabel={(rate) => rate.code!}
              captureMenuScroll={false}
              className={reactSelectStyles.customSelect}
              classNamePrefix="customSelect"
              placeholder={tLabels("distributionRate.placeholder")}
              noOptionsMessage={() => tLabels("distributionRate.noOptionsMessage")}
            />
          </FormGroup>
          {/* Gas */}
          <FormGroup
            labelText={tLabels("consumptionCharacteristics.label")}
            inputId="consumptionCharacteristics"
            validationError={errors["consumptionCharacteristics"]}
            visible={isGas()}
          >
            <Select
              {...bindReactSelectField(
                "consumptionCharacteristics",
                "code",
                consumptionCharacteristics,
                true
              )}
              getOptionLabel={(x) => x.name!}
              hideSelectedOptions={false}
              captureMenuScroll={false}
              className={reactSelectStyles.customSelect}
              classNamePrefix="customSelect"
              placeholder={tLabels("consumptionCharacteristics.placeholder")}
              noOptionsMessage={() => tLabels("consumptionCharacteristics.noOptionsMessage")}
            />
          </FormGroup>
        </div>
        <div className="col-md-6 px-2">
          <FormGroup
            labelText={getYearlyConsumptionTrans(commodity, tLabels, "label")}
            inputId="yearlyConsumption"
            validationError={errors["yearlyConsumption"]}
          >
            <div className="input-group">
              <input
                className="form-control"
                placeholder={getYearlyConsumptionTrans(commodity, tLabels, "placeholder")}
                {...bindFieldNew("yearlyConsumption", true)}
                type="number"
                min={0}
              />
              <div className="input-group-append">
                <span className="input-group-text">MWh</span>
              </div>
            </div>
          </FormGroup>
        </div>
        <div className="col-md-6 px-2">
          <FormGroup
            labelText={tLabels("yearlyConsumptionNT.label")}
            inputId="yearlyConsumptionNT"
            validationError={errors["yearlyConsumptionNT"]}
            visible={isMultipleRate()}
          >
            <div className="input-group">
              <input
                className="form-control"
                placeholder={tLabels("yearlyConsumptionNT.placeholder")}
                {...bindFieldNew("yearlyConsumptionNT", true)}
                type="number"
                min={0}
              />
              <div className="input-group-append">
                <span className="input-group-text">MWh</span>
              </div>
            </div>
          </FormGroup>
        </div>
        <RenderWrapper isVisible={isElectricity}>
          <div className="col-md-6 px-2">
            <FormGroup
              labelText={tLabels("phase.label")}
              validationError={errors["phase"]}
            >
              <div className="row mx-n2">
                <div className="col-sm-6 px-2">
                  <RadioBox
                    label={tPhases("1")}
                    {...bindCheckField("phase", "1")}
                    heightAsFormControl
                    className="mb-md-0 justify-content-center"
                  />
                </div>
                <div className="col-sm-6 px-2">
                  <RadioBox
                    label={tPhases("3")}
                    {...bindCheckField("phase", "3")}
                    heightAsFormControl
                    className="mb-md-0 justify-content-center"
                  />
                </div>
              </div>
            </FormGroup>
          </div>
          <div className="col-md-6 px-2">
            <FormGroup
              labelText={tLabels("circuitBreaker.label")}
              inputId="circuitBreaker"
              validationError={errors["circuitBreaker"]}
            >
              <div className="input-group">
                <input
                  className="form-control"
                  placeholder={tLabels("circuitBreaker.placeholder")}
                  {...bindFieldNew("circuitBreaker", true)}
                  type="number"
                  min={0}
                />
                <div className="input-group-append">
                  <span className="input-group-text">A</span>
                </div>
              </div>
            </FormGroup>
          </div>
        </RenderWrapper>
      </div>
    </PageCard>
  );
};

export default PlaceOfDeliveryDataSubForm;
